import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { useCaja } from '../contexts/CajaContext';
import { getApiUrl } from '../config/apiConfig';
import { Loader2, CreditCard, DollarSign, Settings, AlertTriangle, FileSpreadsheet, Eye, Download, Search, X } from 'lucide-react';
import ProductosMasVendidos from './ProductosMasVendidos';
import './Dashboard.css';

const DashboardComponent = () => {
  const { auth } = useAuth();
  const { caja, cajasAbiertas } = useCaja();

  // Modo del filtro: 'turno' (predeterminado) | 'personalizado'
  const [modoFiltro, setModoFiltro] = useState('turno');
  // Inputs datetime-local para rango personalizado (ej: "2026-02-22T10:30")
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  const [dashboardData, setDashboardData] = useState({
    pagos: {
      total: 0,
      efectivo: 0,
      yape: 0,
      plin: 0,
      tarjeta: 0
    },
    rotacion: [],
    ventas: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(false);

  // Historial de Cierres de Caja
  const [historialCierres, setHistorialCierres] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [fechaFiltro, setFechaFiltro] = useState('');
  const [offsetHistorial, setOffsetHistorial] = useState(0);
  const [hasMoreHistorial, setHasMoreHistorial] = useState(true);
  const [seccionExpandida, setSeccionExpandida] = useState(false);

  // Modal de Detalle
  const [cajaSeleccionadaId, setCajaSeleccionadaId] = useState(null);
  const [reporteCajaData, setReporteCajaData] = useState(null);
  const [loadingReporteCaja, setLoadingReporteCaja] = useState(false);

  // Modal de Auditoría de Pagos por Método
  const [modalAuditoriaOpen, setModalAuditoriaOpen] = useState(false);
  const [modalVisualizarOpen, setModalVisualizarOpen] = useState(false);
  const [metodoAuditoria, setMetodoAuditoria] = useState(null);
  const [datosAuditoria, setDatosAuditoria] = useState(null);
  const [loadingAuditoria, setLoadingAuditoria] = useState(false);
  const [errorAuditoria, setErrorAuditoria] = useState(null);
  const [busquedaAuditoria, setBusquedaAuditoria] = useState('');

  // Colores para los gráficos - Paleta moderna
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Fecha de apertura de la PRIMERA caja (cajasAbiertas viene ORDER BY fecha_apertura ASC)
  // Si hay dos cajas abiertas, se muestra desde que abrió la primera
  const primeraFecha = cajasAbiertas[0]?.fecha_apertura || null;

  useEffect(() => {
    // Esperar a que CajaContext termine su fetch antes de llamar al dashboard.
    if (!auth?.token || caja.loading) return;

    if (primeraFecha) {
      fetchDashboardData(primeraFecha);
    } else {
      // No hay caja abierta: detener el spinner sin hacer fetch del histórico completo
      setIsLoading(false);
    }
  }, [auth?.token, primeraFecha, caja.loading]);

  const fetchDashboardData = async (inicio = null, fin = null) => {
    try {
      setIsLoading(true);
      setError(null);
      setAuthError(false);

      // Verificar si hay token disponible
      if (!auth?.token) {
        setAuthError(true);
        throw new Error('No se encontró token de autenticación');
      }

      // Construir URL con filtros de fecha opcionales
      let dashboardUrl = getApiUrl('/detalles/dashboard');
      const qp = new URLSearchParams();
      if (inicio) qp.append('fecha_inicio', inicio);
      if (fin)    qp.append('fecha_fin', fin);
      if (qp.toString()) dashboardUrl += '?' + qp.toString();

      const response = await fetch(dashboardUrl, {
        headers: {
          'Authorization': `Bearer ${auth.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403 || response.status === 401) {
        setAuthError(true);
        throw new Error('Error de autenticación: No tienes permiso para acceder a estos datos');
      }

      if (!response.ok) {
        throw new Error(`Error al obtener datos del dashboard: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Procesar los datos de rotación
      if (data.rotacion && Array.isArray(data.rotacion)) {
        data.rotacion = data.rotacion.map(item => ({
          ...item,
          cantidad: Number(item.cantidad) || 0,
          total: Number(item.monto) || 0,
          monto: Number(item.monto) || 0
        }));

        // Ordenar por cantidad
        data.rotacion.sort((a, b) => b.cantidad - a.cantidad);
      }

      // Asegurarse de que los valores de pagos sean números
      if (data.pagos) {
        data.pagos = {
          total: Number(data.pagos.total) || 0,
          efectivo: Number(data.pagos.efectivo) || 0,
          yape: Number(data.pagos.yape) || 0,
          plin: Number(data.pagos.plin) || 0,
          tarjeta: Number(data.pagos.tarjeta) || 0
        };
      }

      setDashboardData(data);

    } catch (err) {
      console.error('Error al cargar datos del dashboard:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // ===== FUNCIONES DE FILTRO =====
  const aplicarFiltroPersonalizado = () => {
    if (!fechaDesde || !fechaHasta) return;
    const inicio = fechaDesde.includes('T') ? fechaDesde.replace('T', ' ') + ':00' : fechaDesde + ' 00:00:00';
    const fin = fechaHasta.includes('T') ? fechaHasta.replace('T', ' ') + ':59' : fechaHasta + ' 23:59:59';
    fetchDashboardData(inicio, fin);
  };

  const volverATurnoActual = () => {
    setModoFiltro('turno');
    setFechaDesde('');
    setFechaHasta('');
    if (primeraFecha) {
      fetchDashboardData(primeraFecha);
    } else {
      // Sin turno activo: resetear a estado vacío sin fetchear histórico
      setDashboardData({ pagos: { total: 0, efectivo: 0, yape: 0, plin: 0, tarjeta: 0 }, rotacion: [], ventas: [] });
      setIsLoading(false);
    }
  };

  // ===== HISTORIAL DE CIERRES DE CAJA =====
  const fetchHistorialCierres = async (reset = false, searchDate = fechaFiltro) => {
    if (!auth?.token) return;
    setLoadingHistorial(true);
    try {
      const nextOffset = reset ? 0 : offsetHistorial;
      let url = getApiUrl(`/caja/historial?limit=7&offset=${nextOffset}`);
      if (searchDate) {
        url += `&fecha=${searchDate}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (reset) {
          setHistorialCierres(data);
          setOffsetHistorial(data.length);
        } else {
          setHistorialCierres(prev => [...prev, ...data]);
          setOffsetHistorial(prev => prev + data.length);
        }
        if (data.length < 7) {
          setHasMoreHistorial(false);
        } else {
          setHasMoreHistorial(true);
        }
      }
    } catch (err) {
      console.error('Error al cargar historial de cierres:', err);
    } finally {
      setLoadingHistorial(false);
    }
  };

  useEffect(() => {
    if (seccionExpandida) {
      fetchHistorialCierres(true, fechaFiltro);
    }
  }, [seccionExpandida, fechaFiltro]);

  const verDetalleCierre = async (idCaja) => {
    setCajaSeleccionadaId(idCaja);
    setLoadingReporteCaja(true);
    setReporteCajaData(null);
    try {
      const response = await fetch(getApiUrl(`/caja/reporte?id_caja=${idCaja}`), {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReporteCajaData(data);
      }
    } catch (err) {
      console.error('Error al cargar reporte de caja:', err);
    } finally {
      setLoadingReporteCaja(false);
    }
  };

  // ===== FUNCIONES DE AUDITORÍA DE MÉTODOS DE PAGO =====
  const abrirAuditoriaMetodo = (metodoKey) => {
    setMetodoAuditoria(metodoKey);
    setDatosAuditoria(null);
    setErrorAuditoria(null);
    setModalAuditoriaOpen(true);
  };

  const getRangoAuditoriaParams = () => {
    const qp = new URLSearchParams();
    if (modoFiltro === 'turno') {
      if (primeraFecha) qp.append('fecha_inicio', primeraFecha);
    } else if (modoFiltro === 'personalizado') {
      if (fechaDesde) {
        const inicio = fechaDesde.includes('T') ? fechaDesde.replace('T', ' ') + ':00' : fechaDesde + ' 00:00:00';
        qp.append('fecha_inicio', inicio);
      }
      if (fechaHasta) {
        const fin = fechaHasta.includes('T') ? fechaHasta.replace('T', ' ') + ':59' : fechaHasta + ' 23:59:59';
        qp.append('fecha_fin', fin);
      }
    }
    return qp.toString();
  };

  const obtenerDatosAuditoria = async (metodoKey) => {
    if (!auth?.token || !metodoKey) return null;
    try {
      setLoadingAuditoria(true);
      setErrorAuditoria(null);
      
      let url = getApiUrl(`/detalles/auditoria-pagos?metodo=${encodeURIComponent(metodoKey)}`);
      const extraParams = getRangoAuditoriaParams();
      if (extraParams) url += '&' + extraParams;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${auth.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error al obtener auditoría: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setDatosAuditoria(data);
      return data;
    } catch (err) {
      console.error('Error al obtener auditoría de pagos:', err);
      setErrorAuditoria(err.message);
      return null;
    } finally {
      setLoadingAuditoria(false);
    }
  };

  const exportarAuditoriaExcel = async (datosOverride = null) => {
    let data = datosOverride || datosAuditoria;
    if (!data || data.metodo !== metodoAuditoria) {
      data = await obtenerDatosAuditoria(metodoAuditoria);
    }
    if (!data || !data.transacciones || data.transacciones.length === 0) {
      alert(`No hay transacciones registradas de ${metodoAuditoria?.toUpperCase()} en este periodo.`);
      return;
    }

    const nombreMetodo = (metodoAuditoria || '').toUpperCase();
    const wb = XLSX.utils.book_new();

    const rangoTexto = modoFiltro === 'turno'
      ? `Turno Actual (Desde ${primeraFecha ? new Date(primeraFecha).toLocaleString('es-PE') : 'Apertura'})`
      : `Del ${fechaDesde || 'Inicio'} al ${fechaHasta || 'Fin'}`;

    const filas = [
      [`REPORTE DE AUDITORÍA DE PAGOS — ${nombreMetodo}`],
      [`Periodo: ${rangoTexto}`],
      [`Fecha de emisión: ${new Date().toLocaleString('es-PE')}`],
      [`Total Recaudado: S/ ${formatCurrency(data.total_recaudado)} (${data.total_transacciones} operaciones)`],
      [],
      ['ID Pedido', 'Mesa / Referencia', 'Fecha y Hora', 'Modalidad', 'Monto Cobrado (S/)', 'Total Cuenta (S/)', 'Cajero / Atendido por', 'Detalle Producto']
    ];

    data.transacciones.forEach(t => {
      filas.push([
        t.id_pedido,
        t.referencia,
        new Date(t.fecha_hora).toLocaleString('es-PE'),
        t.modalidad,
        Number(t.monto_metodo) || 0,
        Number(t.total_pedido) || 0,
        t.cajero,
        t.detalle_producto || '-'
      ]);
    });

    filas.push([]);
    filas.push(['TOTAL', '', '', '', Number(data.total_recaudado) || 0, '', '', '']);

    const ws = XLSX.utils.aoa_to_sheet(filas);
    ws['!cols'] = [
      { wch: 12 },
      { wch: 20 },
      { wch: 22 },
      { wch: 22 },
      { wch: 18 },
      { wch: 18 },
      { wch: 22 },
      { wch: 26 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, `Auditoria_${nombreMetodo}`);
    const fechaStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `auditoria_pagos_${metodoAuditoria}_${fechaStr}.xlsx`);

    setModalAuditoriaOpen(false);
  };

  const handleVisualizarEnPantalla = async () => {
    setModalAuditoriaOpen(false);
    setModalVisualizarOpen(true);
    setBusquedaAuditoria('');
    await obtenerDatosAuditoria(metodoAuditoria);
  };

  const imprimirReporte = (reporteData) => {
    if (!reporteData) return;

    const ventana = window.open('', '_blank', 'width=800,height=600');
    ventana.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Reporte de Cierre de Caja</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 13px; color: #333; margin: 24px; }
          h2 { color: #f05b41; font-size: 1.2rem; margin: 0 0 6px; }
          h4 { font-size: 0.95rem; text-transform: uppercase; letter-spacing: 0.5px;
               border-bottom: 1px solid #eee; padding-bottom: 4px; margin: 14px 0 8px; }
          p { margin: 3px 0; font-size: 0.9rem; color: #555; }
          .encabezado { border-bottom: 2px solid #f05b41; padding-bottom: 10px; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; font-size: 0.88rem; margin-bottom: 10px; }
          th { background: #f5f5f5; font-weight: 600; text-align: left;
               padding: 7px 10px; border-bottom: 2px solid #ddd; }
          td { padding: 6px 10px; border-bottom: 1px solid #f0f0f0; }
          .pagos { max-width: 360px; }
          tfoot td { font-weight: bold; border-top: 2px solid #f05b41;
                     font-size: 0.95rem; color: #f05b41; }
        </style>
      </head>
      <body>
        <div class="encabezado">
          <h2>Reporte de Cierre de Caja</h2>
          <p><strong>Cajero:</strong> ${reporteData.cajero || '-'}</p>
          <p><strong>Apertura:</strong> ${new Date(reporteData.fecha_apertura).toLocaleString()}</p>
          <p><strong>Cierre:</strong> ${reporteData.fecha_cierre ? new Date(reporteData.fecha_cierre).toLocaleString() : '-'}</p>
        </div>

        <h4>Ventas por Producto</h4>
        <table>
          <thead><tr><th>Producto</th><th>Cantidad</th><th>Precio Unit.</th><th>Subtotal</th></tr></thead>
          <tbody>
            ${reporteData.productos.map(p => `
              <tr>
                <td>${p.nombre}</td>
                <td>${p.cantidad_total}</td>
                <td>S/. ${parseFloat(p.precio_unitario).toFixed(2)}</td>
                <td>S/. ${parseFloat(p.subtotal).toFixed(2)}</td>
              </tr>`).join('')}
          </tbody>
        </table>

        <h4>Resumen por Método de Pago</h4>
        <table class="pagos">
          <thead><tr><th>Método de Pago</th><th>Total</th></tr></thead>
          <tbody>
            ${reporteData.monto_apertura > 0 ? `
              <tr>
                <td>🏦 Fondo de Apertura</td>
                <td>S/. ${parseFloat(reporteData.monto_apertura).toFixed(2)}</td>
              </tr>` : ''}
            ${Object.entries(reporteData.resumen_pagos).map(([metodo, total]) => `
              <tr>
                <td>${metodo.charAt(0).toUpperCase() + metodo.slice(1)}</td>
                <td>S/. ${parseFloat(total).toFixed(2)}</td>
              </tr>`).join('')}
          </tbody>
          <tfoot>
            <tr><td>TOTAL EN CAJA</td><td>S/. ${(parseFloat(reporteData.total_general) + parseFloat(reporteData.monto_apertura || 0)).toFixed(2)}</td></tr>
          </tfoot>
        </table>

        ${reporteData.cancelaciones && reporteData.cancelaciones.length > 0 ? `
          <h4>Auditoría de Cancelaciones</h4>
          <table>
            <thead>
              <tr>
                <th>Mesa/Pedido</th>
                <th>Producto</th>
                <th>Cant.</th>
                <th>Motivo</th>
                <th>Cancelado por</th>
                <th>Fecha/Hora</th>
              </tr>
            </thead>
            <tbody>
              ${reporteData.cancelaciones.map(c => `
                <tr>
                  <td>${c.referencia_mesa} ${c.id_pedido ? `(Ped. #${c.id_pedido})` : ''}</td>
                  <td>${c.nombre_producto}</td>
                  <td>${c.cantidad}</td>
                  <td>${c.motivo}</td>
                  <td>${c.usuario_cancelo || '-'}</td>
                  <td>${new Date(c.fecha_cancelacion).toLocaleString()}</td>
                </tr>`).join('')}
            </tbody>
          </table>
        ` : ''}
      </body>
      </html>
    `);
    ventana.document.close();
    ventana.focus();
    ventana.print();
  };

  // Datos para el gráfico de barras de métodos de pago
  const pagosPorMetodo = [
    { name: 'Efectivo', value: dashboardData.pagos.efectivo },
    { name: 'Yape', value: dashboardData.pagos.yape },
    { name: 'Plin', value: dashboardData.pagos.plin },
    { name: 'Tarjeta', value: dashboardData.pagos.tarjeta }
  ].filter(item => item.value > 0);

  // Formatear número como moneda
  const formatCurrency = (value) => {
    const numValue = Number(value);
    if (isNaN(numValue)) return '0.00';

    return new Intl.NumberFormat('es-PE', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  };

  // Encuentra el método de pago preferido
  const metodoPreferedido = pagosPorMetodo.reduce((prev, current) =>
    (prev.value > current.value) ? prev : current, { name: 'N/A', value: 0 }).name;

  // Verificar si hay datos para mostrar (calculado siempre, no tras early-returns)
  const hayPagos = dashboardData.pagos.total > 0 ||
                  dashboardData.pagos.efectivo > 0 ||
                  dashboardData.pagos.yape > 0 ||
                  dashboardData.pagos.plin > 0 ||
                  dashboardData.pagos.tarjeta > 0;

  const hayProductos = dashboardData.rotacion &&
                       dashboardData.rotacion.length > 0 &&
                       dashboardData.rotacion.some(p => p.cantidad > 0);

  // Transacciones filtradas para la vista modal de auditoría
  const transaccionesFiltradas = (datosAuditoria?.transacciones || []).filter(t => {
    if (!busquedaAuditoria.trim()) return true;
    const q = busquedaAuditoria.toLowerCase().trim();
    return (
      (t.referencia && t.referencia.toLowerCase().includes(q)) ||
      (t.id_pedido && t.id_pedido.toString().includes(q)) ||
      (t.cajero && t.cajero.toLowerCase().includes(q)) ||
      (t.modalidad && t.modalidad.toLowerCase().includes(q))
    );
  });

  // ===== SINGLE RETURN — header y barra de filtros siempre visibles =====
  return (
    <div className="dashboard-container">

      {/* Header — siempre visible */}
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <div className="dashboard-last-update">
          Última actualización: {new Date().toLocaleString('es-PE')}
        </div>
      </div>

      {/* Barra de filtros — siempre visible */}
      <div className="dashboard-filtros">
        <div className="filtro-tabs-dash">
          <button
            className={`filtro-tab-btn ${modoFiltro === 'turno' ? 'activo' : ''}`}
            onClick={volverATurnoActual}
          >
            🕐 Turno Actual
          </button>
          <button
            className={`filtro-tab-btn ${modoFiltro === 'personalizado' ? 'activo' : ''}`}
            onClick={() => setModoFiltro('personalizado')}
          >
            📅 Rango Personalizado
          </button>
        </div>

        {modoFiltro === 'turno' && (
          <div className="filtro-info-turno">
            {primeraFecha
              ? <>Desde: <strong>{new Date(primeraFecha).toLocaleString('es-PE')}</strong> — hasta ahora
                  {cajasAbiertas.length > 1 && <span style={{color:'#94a3b8', marginLeft:'8px', fontSize:'12px'}}>(primera de {cajasAbiertas.length} cajas abiertas)</span>}
                </>
              : <span className="filtro-sin-caja">Sin caja abierta — mostrando todos los datos</span>
            }
          </div>
        )}

        {modoFiltro === 'personalizado' && (
          <div className="filtro-personalizado-dash">
            <label>Desde:</label>
            <input
              type="datetime-local"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              max={fechaHasta || undefined}
            />
            <label>Hasta:</label>
            <input
              type="datetime-local"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              min={fechaDesde || undefined}
            />
            <button
              className="filtro-btn-aplicar"
              onClick={aplicarFiltroPersonalizado}
              disabled={!fechaDesde || !fechaHasta}
            >
              Aplicar
            </button>
          </div>
        )}
      </div>

      {/* ===== CONTENIDO CONDICIONAL ===== */}

      {/* Cargando */}
      {isLoading && (
        <div className="loading-container">
          <Loader2 className="w-12 h-12 loading-icon animate-spin mb-4" />
          <p className="loading-text">Cargando datos del dashboard...</p>
        </div>
      )}

      {/* Error de autenticación */}
      {!isLoading && authError && (
        <div className="error-container">
          <div className="error-header">
            <AlertTriangle className="h-6 w-6 error-icon" />
            <div>
              <p className="error-title">Error de autenticación</p>
              <p className="error-message">No tienes permisos para acceder a los datos del dashboard o tu sesión ha expirado.</p>
              <p className="error-action">Por favor, inicia sesión nuevamente.</p>
              <button
                className="retry-button"
                onClick={() => window.location.href = '/login'}
              >
                Ir a la página de login
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error general */}
      {!isLoading && !authError && error && (
        <div className="error-container">
          <div className="error-header">
            <AlertTriangle className="h-6 w-6 error-icon" />
            <div>
              <p className="error-title">Error al cargar los datos</p>
              <p className="error-message">{error}</p>
              <p className="error-action">Por favor, intenta nuevamente más tarde.</p>
              <button
                className="retry-button"
                onClick={() => window.location.reload()}
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sin turno activo — CajaContext ya cargó pero no hay cajas abiertas (solo en modo turno) */}
      {!isLoading && !authError && !error && !caja.loading && !primeraFecha && modoFiltro === 'turno' && (
        <div className="no-data-container">
          <div className="no-data-header">
            <AlertTriangle className="h-6 w-6 no-data-icon" />
            <div>
              <p className="no-data-title">Sin turno activo</p>
              <p className="no-data-message">No hay ninguna caja abierta. Abre una caja para ver los datos del turno actual, o usa el filtro de Rango Personalizado para consultar fechas anteriores.</p>
            </div>
          </div>
        </div>
      )}

      {/* Sin datos — hay caja o se aplicó rango personalizado, pero no hay ventas */}
      {!isLoading && !authError && !error && (primeraFecha || modoFiltro === 'personalizado') &&
        !hayPagos && !hayProductos &&
        (!dashboardData.ventas || dashboardData.ventas.length === 0) && (
        <div className="no-data-container">
          <div className="no-data-header">
            <Settings className="h-6 w-6 no-data-icon" />
            <div>
              <p className="no-data-title">No hay datos para mostrar</p>
              <p className="no-data-message">No hay ventas registradas para el período seleccionado o todos los pedidos están pendientes.</p>
            </div>
          </div>
        </div>
      )}

      {/* Contenido principal — solo cuando hay datos */}
      {!isLoading && !authError && !error && (hayPagos || hayProductos) && (
        <>
          {/* Tarjetas de resumen */}
          <div className="summary-cards">
            <div className="summary-card card-blue">
              <div className="summary-card-header">
                <DollarSign className="summary-card-icon h-5 w-5" />
                <h3 className="summary-card-title">Total Ventas</h3>
              </div>
              <p className="summary-card-value">S/ {formatCurrency(dashboardData.pagos.total)}</p>
            </div>

            <div className="summary-card card-green">
              <div className="summary-card-header">
                <CreditCard className="summary-card-icon h-5 w-5" />
                <h3 className="summary-card-title">Método Preferido</h3>
              </div>
              <p className="summary-card-value">{metodoPreferedido}</p>
            </div>

            {hayProductos && (
              <div className="summary-card card-amber">
                <div className="summary-card-header">
                  <Settings className="summary-card-icon h-5 w-5" />
                  <h3 className="summary-card-title">Producto Estrella</h3>
                </div>
                <p className="summary-card-value">{dashboardData.rotacion[0]?.nombre || 'N/A'}</p>
              </div>
            )}
          </div>

          {/* Grid para los gráficos de pagos y productos */}
          <div className="metrics-grid">

            {/* Sección de Pagos */}
            {hayPagos && (
              <div className="content-section">
                <h2 className="section-title">Métodos de Pago</h2>

                <div className="space-y-6">
                  {/* Datos textuales — Clicables para auditar */}
                  <div className="data-grid">
                    <div 
                      className="data-item data-item-clickable"
                      onClick={() => abrirAuditoriaMetodo('efectivo')}
                      title="Haz clic para auditar o exportar pagos en Efectivo"
                    >
                      <div className="data-item-header">
                        <p className="data-label">Efectivo</p>
                        <span className="data-badge-audit badge-blue">Auditar</span>
                      </div>
                      <p className="data-value">S/ {formatCurrency(dashboardData.pagos.efectivo)}</p>
                    </div>

                    <div 
                      className="data-item data-item-clickable"
                      onClick={() => abrirAuditoriaMetodo('yape')}
                      title="Haz clic para auditar o exportar pagos con Yape"
                    >
                      <div className="data-item-header">
                        <p className="data-label">Yape</p>
                        <span className="data-badge-audit badge-purple">Auditar</span>
                      </div>
                      <p className="data-value">S/ {formatCurrency(dashboardData.pagos.yape)}</p>
                    </div>

                    <div 
                      className="data-item data-item-clickable"
                      onClick={() => abrirAuditoriaMetodo('plin')}
                      title="Haz clic para auditar o exportar pagos con Plin"
                    >
                      <div className="data-item-header">
                        <p className="data-label">Plin</p>
                        <span className="data-badge-audit badge-teal">Auditar</span>
                      </div>
                      <p className="data-value">S/ {formatCurrency(dashboardData.pagos.plin)}</p>
                    </div>

                    <div 
                      className="data-item data-item-clickable"
                      onClick={() => abrirAuditoriaMetodo('tarjeta')}
                      title="Haz clic para auditar o exportar pagos con Tarjeta"
                    >
                      <div className="data-item-header">
                        <p className="data-label">Tarjeta</p>
                        <span className="data-badge-audit badge-amber">Auditar</span>
                      </div>
                      <p className="data-value">S/ {formatCurrency(dashboardData.pagos.tarjeta)}</p>
                    </div>
                  </div>

                  {/* Gráfico de barras */}
                  <div className="chart-container" style={{ height: "300px", width: "100%" }}>
                    {pagosPorMetodo.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={pagosPorMetodo}
                          margin={{ top: 30, right: 20, left: 20, bottom: 30 }}
                          barSize={40}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="name"
                            tick={{ fill: '#64748b' }}
                            axisLine={{ stroke: '#e2e8f0' }}
                            tickLine={{ stroke: '#e2e8f0' }}
                          />
                          <YAxis
                            tick={{ fill: '#64748b' }}
                            tickFormatter={(value) => `S/ ${formatCurrency(value)}`}
                            axisLine={{ stroke: '#e2e8f0' }}
                            tickLine={{ stroke: '#e2e8f0' }}
                            domain={[0, 'dataMax * 1.2']}
                          />
                          <Tooltip
                            formatter={(value) => [`S/ ${formatCurrency(value)}`, 'Total']}
                            contentStyle={{
                              backgroundColor: '#fff',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
                            }}
                            labelStyle={{ fontWeight: 'bold', color: '#334155', marginBottom: '5px' }}
                          />
                          <Bar dataKey="value">
                            {pagosPorMetodo.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        No hay pagos registrados
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Sección de Productos más vendidos */}
            {hayProductos && (
              <ProductosMasVendidos rotacionData={dashboardData.rotacion} />
            )}
          </div>

          {/* ── SECCIÓN: HISTORIAL DE CIERRES DE CAJA ──────────────────────── */}
          <div className="historial-cierres-seccion">
            <div 
              className="historial-cierres-header"
              onClick={() => setSeccionExpandida(!seccionExpandida)}
            >
              <h3>📋 Historial de Cierres de Caja</h3>
              <span className={`accordion-arrow ${seccionExpandida ? 'expanded' : ''}`}>▼</span>
            </div>

            {seccionExpandida && (
              <div className="historial-cierres-content">
                {/* Filtro por fecha */}
                <div className="historial-filtros">
                  <div className="filtro-campo">
                    <label>Buscar por fecha de cierre:</label>
                    <input 
                      type="date"
                      value={fechaFiltro}
                      onChange={(e) => setFechaFiltro(e.target.value)}
                    />
                  </div>
                  {fechaFiltro && (
                    <button 
                      className="limpiar-filtro-btn"
                      onClick={() => setFechaFiltro('')}
                    >
                      Limpiar
                    </button>
                  )}
                </div>

                {/* Listado */}
                {loadingHistorial && historialCierres.length === 0 ? (
                  <div className="loading-historial">Cargando historial...</div>
                ) : (
                  <>
                    {historialCierres.length === 0 ? (
                      <div className="no-cierres-message">No se encontraron cierres de caja.</div>
                    ) : (
                      <div className="tabla-cierres-wrapper">
                        <table className="tabla-cierres">
                          <thead>
                            <tr>
                              <th>Caja</th>
                              <th>Cajero</th>
                              <th>Piso</th>
                              <th>Monto Cierre</th>
                              <th>Apertura</th>
                              <th>Cierre</th>
                              <th>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {historialCierres.map((c) => (
                              <tr key={c.id}>
                                <td className="font-bold">{c.nombre}</td>
                                <td>{c.cajero || '-'}</td>
                                <td>
                                  <span className={`piso-tag piso-${c.piso}`}>
                                    {c.piso === 0 ? 'Piso 0 (Directos)' : `Piso ${c.piso}`}
                                  </span>
                                </td>
                                <td className="monto-cierre font-bold">
                                  S/. {formatCurrency(c.monto_cierre_total)}
                                </td>
                                <td className="fecha-caja">
                                  {new Date(c.fecha_apertura).toLocaleDateString()} {new Date(c.fecha_apertura).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </td>
                                <td className="fecha-caja">
                                  {new Date(c.fecha_cierre).toLocaleDateString()} {new Date(c.fecha_cierre).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </td>
                                <td>
                                  <button 
                                    className="ver-reporte-btn"
                                    onClick={() => verDetalleCierre(c.id)}
                                  >
                                    Ver Reporte / Imprimir
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Botón Ver Más */}
                    {hasMoreHistorial && (
                      <div className="ver-mas-container">
                        <button 
                          className="ver-mas-btn"
                          onClick={() => fetchHistorialCierres(false, fechaFiltro)}
                          disabled={loadingHistorial}
                        >
                          {loadingHistorial ? 'Cargando...' : 'Ver más cierres'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── MODAL: DETALLE DE REPORTE DE CAJA (IMPRIMIBLE) ──────────────── */}
          {cajaSeleccionadaId && (
            <div className="modal-overlay-cierre">
              <div className="modal-content-cierre">
                <div className="modal-header-cierre">
                  <h2>Detalle de Cierre de Caja</h2>
                  <button className="close-x-btn" onClick={() => setCajaSeleccionadaId(null)}>×</button>
                </div>

                <div className="modal-body-cierre">
                  {loadingReporteCaja ? (
                    <div className="loading-reporte">Cargando reporte...</div>
                  ) : !reporteCajaData ? (
                    <div className="error-reporte">No se pudo obtener el reporte de la caja.</div>
                  ) : (
                    <div className="reporte-detalle-wrapper">
                      <div className="reporte-cabecera">
                        <p><strong>Caja:</strong> {reporteCajaData.nombre}</p>
                        <p><strong>Cajero:</strong> {reporteCajaData.cajero || '-'}</p>
                        <p><strong>Apertura:</strong> {new Date(reporteCajaData.fecha_apertura).toLocaleString()}</p>
                        <p><strong>Cierre:</strong> {reporteCajaData.fecha_cierre ? new Date(reporteCajaData.fecha_cierre).toLocaleString() : '-'}</p>
                      </div>

                      <div className="reporte-seccion">
                        <h4>Resumen por Métodos de Pago</h4>
                        <table className="tabla-modal-reporte">
                          <thead>
                            <tr>
                              <th>Método de Pago</th>
                              <th>Total Recaudado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reporteCajaData.monto_apertura > 0 && (
                              <tr>
                                <td>🏦 Fondo de Apertura</td>
                                <td>S/. {formatCurrency(reporteCajaData.monto_apertura)}</td>
                              </tr>
                            )}
                            {Object.entries(reporteCajaData.resumen_pagos).map(([metodo, total]) => (
                              <tr key={metodo}>
                                <td className="capitalize">{metodo}</td>
                                <td>S/. {formatCurrency(total)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td>TOTAL EN CAJA</td>
                              <td className="total-destacado">
                                S/. {formatCurrency(parseFloat(reporteCajaData.total_general) + parseFloat(reporteCajaData.monto_apertura || 0))}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      <div className="reporte-seccion">
                        <h4>Ventas por Producto</h4>
                        <div className="productos-reporte-scroll">
                          <table className="tabla-modal-reporte">
                            <thead>
                              <tr>
                                <th>Producto</th>
                                <th>Cant.</th>
                                <th>Precio</th>
                                <th>Subtotal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reporteCajaData.productos.map((p, idx) => (
                                  <tr key={idx}>
                                    <td>{p.nombre}</td>
                                    <td>{p.cantidad_total}</td>
                                    <td>S/. {formatCurrency(p.precio_unitario)}</td>
                                    <td>S/. {formatCurrency(p.subtotal)}</td>
                                  </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {reporteCajaData.cancelaciones && reporteCajaData.cancelaciones.length > 0 && (
                        <div className="reporte-seccion" style={{ marginTop: '20px' }}>
                          <h4>Auditoría de Cancelaciones</h4>
                          <div className="productos-reporte-scroll">
                            <table className="tabla-modal-reporte">
                              <thead>
                                <tr>
                                  <th>Mesa/Pedido</th>
                                  <th>Producto</th>
                                  <th>Cant.</th>
                                  <th>Motivo</th>
                                  <th>Cancelado por</th>
                                  <th>Fecha/Hora</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reporteCajaData.cancelaciones.map((c, idx) => (
                                  <tr key={idx}>
                                    <td>{c.referencia_mesa} {c.id_pedido ? `(Ped. #${c.id_pedido})` : ''}</td>
                                    <td>{c.nombre_producto}</td>
                                    <td>{c.cantidad}</td>
                                    <td>{c.motivo}</td>
                                    <td>{c.usuario_cancelo || '-'}</td>
                                    <td>{new Date(c.fecha_cancelacion).toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="modal-footer-cierre">
                  <button className="cancel-btn" onClick={() => setCajaSeleccionadaId(null)}>Cerrar</button>
                  {reporteCajaData && (
                    <button className="print-btn" onClick={() => imprimirReporte(reporteCajaData)}>
                      🖨️ Imprimir Reporte
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Modal 1: Elección de acción para auditoría de método de pago */}
          {modalAuditoriaOpen && (
            <div className="modal-overlay-auditoria" onClick={() => setModalAuditoriaOpen(false)}>
              <div className="modal-content-auditoria" onClick={(e) => e.stopPropagation()}>
                <div className="modal-auditoria-header">
                  <div className="modal-auditoria-title-box">
                    <span className={`metodo-badge-pill pill-${metodoAuditoria}`}>
                      {metodoAuditoria?.toUpperCase()}
                    </span>
                    <h3>Auditoría de Pagos</h3>
                  </div>
                  <button 
                    className="modal-btn-close"
                    onClick={() => setModalAuditoriaOpen(false)}
                    title="Cerrar"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="modal-auditoria-contexto">
                  <p className="contexto-rango">
                    📅 <strong>Rango activo:</strong> {
                      modoFiltro === 'turno' 
                        ? `Turno Actual (desde ${primeraFecha ? new Date(primeraFecha).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }) : 'inicio'})`
                        : `Personalizado (${fechaDesde ? new Date(fechaDesde).toLocaleDateString('es-PE') : ''} - ${fechaHasta ? new Date(fechaHasta).toLocaleDateString('es-PE') : ''})`
                    }
                  </p>
                  <div className="contexto-monto">
                    <span>Monto Registrado:</span>
                    <strong>S/ {formatCurrency(dashboardData.pagos[metodoAuditoria] || 0)}</strong>
                  </div>
                </div>

                <p className="modal-auditoria-pregunta">¿Cómo deseas consultar este reporte?</p>

                <div className="opciones-auditoria-grid">
                  <div 
                    className="opcion-auditoria-card opcion-excel"
                    onClick={() => exportarAuditoriaExcel()}
                  >
                    <div className="opcion-icon-box icon-excel">
                      <FileSpreadsheet className="h-8 w-8 text-emerald-600" />
                    </div>
                    <div className="opcion-info">
                      <h4>Descargar Excel (.xlsx)</h4>
                      <p>Genera y descarga la hoja de cálculo completa con todas las transacciones detalladas.</p>
                    </div>
                    <button className="btn-opcion-accion btn-descargar-excel">
                      <Download className="h-4 w-4 mr-1" /> Descargar Excel
                    </button>
                  </div>

                  <div 
                    className="opcion-auditoria-card opcion-ver"
                    onClick={handleVisualizarEnPantalla}
                  >
                    <div className="opcion-icon-box icon-ver">
                      <Eye className="h-8 w-8 text-blue-600" />
                    </div>
                    <div className="opcion-info">
                      <h4>Visualizar en Pantalla</h4>
                      <p>Abre una tabla interactiva con buscador rápido por mesa, número de pedido o cajero.</p>
                    </div>
                    <button className="btn-opcion-accion btn-ver-pantalla">
                      <Eye className="h-4 w-4 mr-1" /> Ver en Pantalla
                    </button>
                  </div>
                </div>

                <div className="modal-auditoria-footer">
                  <button 
                    className="btn-cancelar-auditoria"
                    onClick={() => setModalAuditoriaOpen(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal 2: Visualización detallada de transacciones */}
          {modalVisualizarOpen && (
            <div className="modal-overlay-auditoria" onClick={() => setModalVisualizarOpen(false)}>
              <div className="modal-content-auditoria-detalle" onClick={(e) => e.stopPropagation()}>
                <div className="modal-auditoria-header">
                  <div className="modal-auditoria-title-box">
                    <span className={`metodo-badge-pill pill-${metodoAuditoria}`}>
                      {metodoAuditoria?.toUpperCase()}
                    </span>
                    <div>
                      <h3>Detalle de Transacciones</h3>
                      <span className="subtitulo-detalle">
                        {modoFiltro === 'turno' 
                          ? `Turno Actual (Desde ${primeraFecha ? new Date(primeraFecha).toLocaleString('es-PE') : 'Apertura'})`
                          : `Rango: ${fechaDesde} — ${fechaHasta}`
                        }
                      </span>
                    </div>
                  </div>
                  <button 
                    className="modal-btn-close"
                    onClick={() => setModalVisualizarOpen(false)}
                    title="Cerrar"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Barra de herramientas: Búsqueda y Exportar */}
                <div className="toolbar-auditoria-detalle">
                  <div className="busqueda-auditoria-box">
                    <Search className="h-4 w-4 busqueda-icon" />
                    <input
                      type="text"
                      placeholder="Buscar por mesa, # pedido o cajero..."
                      value={busquedaAuditoria}
                      onChange={(e) => setBusquedaAuditoria(e.target.value)}
                      className="input-busqueda-auditoria"
                    />
                    {busquedaAuditoria && (
                      <button 
                        onClick={() => setBusquedaAuditoria('')}
                        className="btn-limpiar-busqueda"
                        title="Limpiar búsqueda"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  <div className="stats-auditoria-detalle">
                    <div className="stat-chip stat-total">
                      <span>Total:</span>
                      <strong>S/ {formatCurrency(datosAuditoria?.total_recaudado || 0)}</strong>
                    </div>
                    <div className="stat-chip stat-ops">
                      <span>Operaciones:</span>
                      <strong>{transaccionesFiltradas.length}</strong>
                    </div>
                  </div>

                  <button 
                    className="btn-exportar-excel-tabla"
                    onClick={() => exportarAuditoriaExcel()}
                    disabled={loadingAuditoria || !datosAuditoria?.transacciones?.length}
                  >
                    <Download className="h-4 w-4 mr-1" /> Exportar a Excel
                  </button>
                </div>

                {/* Contenido: Tabla de Transacciones */}
                <div className="tabla-auditoria-wrapper">
                  {loadingAuditoria ? (
                    <div className="loading-auditoria-box">
                      <Loader2 className="h-8 w-8 animate-spin loading-icon" />
                      <p>Consultando transacciones...</p>
                    </div>
                  ) : errorAuditoria ? (
                    <div className="error-auditoria-box">
                      <AlertTriangle className="h-8 w-8 text-amber-500 mb-2" />
                      <p>{errorAuditoria}</p>
                      <button 
                        className="retry-button"
                        onClick={() => obtenerDatosAuditoria(metodoAuditoria)}
                      >
                        Reintentar
                      </button>
                    </div>
                  ) : transaccionesFiltradas.length === 0 ? (
                    <div className="vacio-auditoria-box">
                      <p>No se encontraron transacciones registradas para este método en el periodo seleccionado.</p>
                    </div>
                  ) : (
                    <table className="tabla-auditoria-pagos">
                      <thead>
                        <tr>
                          <th># Pedido</th>
                          <th>Mesa / Ref</th>
                          <th>Fecha y Hora</th>
                          <th>Modalidad</th>
                          <th className="th-monto">Monto ({metodoAuditoria?.toUpperCase()})</th>
                          <th className="th-total">Total Pedido</th>
                          <th>Cajero</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transaccionesFiltradas.map((t, idx) => (
                          <tr key={idx}>
                            <td className="td-pedido">#{t.id_pedido}</td>
                            <td className="td-mesa"><strong>{t.referencia}</strong></td>
                            <td className="td-fecha">{new Date(t.fecha_hora).toLocaleString('es-PE')}</td>
                            <td>
                              <span className={`badge-modalidad modalidad-${(t.modalidad || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}`}>
                                {t.modalidad}
                              </span>
                            </td>
                            <td className="td-monto-metodo">S/ {formatCurrency(t.monto_metodo)}</td>
                            <td className="td-total-pedido">S/ {formatCurrency(t.total_pedido)}</td>
                            <td className="td-cajero">{t.cajero}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="modal-auditoria-footer">
                  <button 
                    className="btn-cancelar-auditoria"
                    onClick={() => setModalVisualizarOpen(false)}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Botón para refrescar datos — respeta el filtro activo */}
          <div className="refresh-container">
            <button
              className="refresh-button"
              onClick={() => {
                if (modoFiltro === 'turno') {
                  // Usar primera caja, sin fin
                  fetchDashboardData(primeraFecha || undefined);
                } else if (fechaDesde && fechaHasta) {
                  aplicarFiltroPersonalizado();
                }
              }}
            >
              Actualizar Datos
            </button>
          </div>
        </>
      )}

    </div>
  );
};

export default DashboardComponent;
